import type { TypeSearchPromoBlockFields } from '@lib/generated-types'
import { getIconFromContentful } from '@lib/icons'
import { Button, Card, type ContainerProps, Container, Icon, Typography } from '@shc/ui'
import NextImage from '@components/next-image'
import { AnalyticsLink } from '@components/analytics'
import { richTextRenderFactory } from '@lib/rich-text-options'
import NextLink from 'next/link'
import { linkAttributes } from '@lib/menu-item-mapper'
import { useBuildSearchContext } from '@lib/analytics'

type SearchPromoBlockType = TypeSearchPromoBlockFields & ContainerProps

const InlineVariant = ({
  heading,
  headingLink,
  headingAnchorOrExternalLink,
  description,
  buttonLabel,
  buttonLink,
  buttonAnchorOrExternalLink,
  image,
}: SearchPromoBlockType) => {
  const snowplowSearchContext = useBuildSearchContext()

  return (
    <div className="flex md:gap-5 lg:gap-15 items-center justify-between pt-8 border-t">
      <div className="flex flex-col flex-grow gap-3 justify-between">
        <div className="search-promo">
          {headingLink || headingAnchorOrExternalLink ? (
            <AnalyticsLink
              noUnderline
              {...linkAttributes(headingLink, headingAnchorOrExternalLink)}
              className="font-medium group"
              click={{
                name: 'component_click',
                data: {},
              }}
              snowplow={{
                contexts: [snowplowSearchContext],
              }}>
              <Typography
                as="h2"
                variant="h4"
                className="text-[1.25rem] group-hover:underline font-bold">
                {heading}
              </Typography>
            </AnalyticsLink>
          ) : (
            <Typography as="h2" variant="h4" className="text-[1.25rem] group-hover:underline font-semibold">
              {heading}
            </Typography>
          )}
        </div>
        <Container className="text-sm" containerSize={12}>
          {richTextRenderFactory()(description)}
        </Container>
        {(buttonLink || buttonAnchorOrExternalLink) && buttonLabel && (
          <AnalyticsLink
            as={Button}
            variant="outlined"
            asPassthru={NextLink}
            click={{
              name: 'component_click',
              data: {},
            }}
            {...linkAttributes(buttonLink, buttonAnchorOrExternalLink)}
            className="font-semibold group mt-2 text-sm w-full md:w-fit"
            size="sm"
            width="full"
            snowplow={{
              contexts: [snowplowSearchContext],
            }}>
            {buttonLabel}
          </AnalyticsLink>
        )}
      </div>
      {image && (
        <div
          className="hidden md:block md:w-[288px] md:h-[162px] lg:w-[335px] lg:h-[188px] rounded relative overflow-hidden min-w-0 flex-shrink-0"
          data-testid="responsive-image-wrapper">
          <NextImage
            src={image.fields.image.fields.file.url}
            className="object-cover w-full h-full"
            alt={image.fields.caption ?? 'Promo image'}
            height={image.fields.image.fields.file.details.image.height}
            width={image.fields.image.fields.file.details.image.width}
          />
        </div>
      )}
    </div>
  )
}

const SearchPromoBlock = ({
  internalName,
  variant,
  icon,
  image,
  heading,
  headingLink,
  headingAnchorOrExternalLink,
  description,
  buttonLabel,
  buttonLink,
  buttonAnchorOrExternalLink,
}: SearchPromoBlockType) => {
  
  if (variant === 'inline') {
    return (
      <InlineVariant
        heading={heading}
        headingLink={headingLink}
        headingAnchorOrExternalLink={headingAnchorOrExternalLink}
        description={description}
        buttonLabel={buttonLabel}
        buttonLink={buttonLink}
        buttonAnchorOrExternalLink={buttonAnchorOrExternalLink}
        image={image}
        internalName={internalName}
      />
    )
  }

  return (
    <Card padding="sm" variant="outlined">
      <div className="flex flex-row search-promo">
        <div className="bg-primary-50 rounded-full size-12 min-h-12 min-w-12 mr-4 flex items-center justify-center text-primary-700">
          {icon && <Icon fixedWidth icon={getIconFromContentful(icon)} size="lg" />}
        </div>
        <div className="flex flex-col gap-1">
          <Typography variant="small-body-semibold">{heading}</Typography>
          <Container className="text-sm" containerSize={12}>
            {richTextRenderFactory()(description)}
          </Container>
        </div>
      </div>
    </Card>
  )
}

export default SearchPromoBlock
